import { useEffect, useId, useRef, useState } from 'react';
import { Settings2 } from 'lucide-react';
import type { FocusSettings } from '@/lib/focus/focusAlerts';
import { TapCheckbox } from '@/components/core/common/TapCheckbox';

interface FocusSettingsMenuProps {
  settings: FocusSettings;
  onChange: (settings: FocusSettings) => void;
  /** Stories open it to show the toggles at rest. */
  defaultOpen?: boolean;
}

const ROWS: { key: keyof FocusSettings; label: string; hint: string }[] = [
  {
    key: 'tabTitle',
    label: 'Countdown in the tab title',
    hint: 'See the time from any tab.',
  },
  { key: 'sound', label: 'Soft chime', hint: 'When a block or break ends.' },
  {
    key: 'notifications',
    label: 'Browser notification',
    hint: 'When a block ends in another tab.',
  },
  {
    key: 'immersive',
    label: 'Start blocks in focus mode',
    hint: 'Just the timer, over the app. Esc to leave.',
  },
];

/**
 * The timer's switches, behind a gear in the Focus card - not on the
 * Settings page, because you change these while looking at the timer.
 * Closes on Escape and on a press outside, like the Navbar pill's panel.
 */
export function FocusSettingsMenu({
  settings,
  onChange,
  defaultOpen = false,
}: FocusSettingsMenuProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape here closes the menu, not focus mode behind it.
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div ref={rootRef} className='relative'>
      <button
        ref={buttonRef}
        type='button'
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label='Timer settings'
        className='flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11 max-sm:w-11'
      >
        <Settings2 className='h-4 w-4' aria-hidden='true' />
      </button>
      {open && (
        <div
          id={panelId}
          className='absolute right-0 top-9 z-20 w-64 space-y-3 rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-elevation-2'
        >
          {ROWS.map(row => (
            <SettingRow
              key={row.key}
              label={row.label}
              hint={row.hint}
              checked={settings[row.key]}
              onChange={checked =>
                onChange({ ...settings, [row.key]: checked })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SettingRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className='flex items-start gap-2.5'>
      <TapCheckbox
        id={id}
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className='mt-0.5'
      />
      <label htmlFor={id} className='text-sm'>
        <span className='block font-medium text-foreground'>{label}</span>
        <span className='block text-xs text-muted-foreground'>{hint}</span>
      </label>
    </div>
  );
}
